import { Button, Group, Stack, Text } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { fadeRise } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import { DS_AI_MOTION } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import { formatCount } from '../../../ingestion.utils';
import type { PageCapNoticeState } from '../../useExtractFromDocumentModalController.types';

interface Props {
	notice: PageCapNoticeState;
}

/** A start over the page cap: the cap and the pages, and "Start as n batches", cut by document date. */
export const PageCapNotice = ({ notice }: Props) => {
	const tCapManage = useTranslationCapManage();

	return (
		<Group
			gap="sm"
			p="sm"
			wrap="nowrap"
			bg="orange.0"
			styles={{
				root: {
					borderTop: '1px solid var(--mantine-color-orange-3)',
					animation: `${fadeRise} ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easeOut} both`,
				},
			}}
			role="alert"
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PAGE_CAP_NOTICE}
		>
			<Stack flex={1} gap={4} miw={0}>
				<Text span size="md" fw={600} c="dark.8">
					{tCapManage('dealstack_ai.ingest.page_cap.title')}
				</Text>
				<Text span size="md" c="dark.4">
					{tCapManage('dealstack_ai.ingest.page_cap.message', {
						pages: formatCount(notice.pages),
						cap: formatCount(notice.capPages),
						count: notice.batchCount,
					})}
				</Text>
			</Stack>
			<Group gap="sm" wrap="nowrap">
				<Button
					size="height-36"
					variant="link-color"
					px={0}
					onClick={notice.onDismiss}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PAGE_CAP_DISMISS_BUTTON}
				>
					{tCapManage('dealstack_ai.ingest.page_cap.change_selection')}
				</Button>
				<Button
					size="height-36"
					fw={600}
					loading={notice.isStarting}
					onClick={notice.onStartSplit}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PAGE_CAP_SPLIT_BUTTON}
				>
					{tCapManage('dealstack_ai.ingest.page_cap.start_split', {
						count: notice.batchCount,
					})}
				</Button>
			</Group>
		</Group>
	);
};
